import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { useFetchSmartLinkData } from '../../hooks/use-fetch-smart-link-data';
import { CardVariant, KnowledgeSmartCard } from '../../knowledge-smart-card/knowledge-smart-card';
import type { SmartLinkElementType } from '../types';

interface KnowledgeCardSource {
	ari?: string;
	iconUrl: string | null;
	id: string;
	lastModified: string | null;
	product: string | null;
	spaceName: string | null;
	spaceUrl: string | null;
	title: string;
	type: string;
	url: string;
}

interface SmartLinkComponentProps {
	component: SmartLinkElementType;
	isStandalone?: boolean;
	variant?: CardVariant;
}

const styles = cssMap({
	smartLink: {
		width: '336px',
	},
});

/**
 * Convert SmartLinkElement to KnowledgeCardSource format
 */
function convertToKnowledgeCardSource(
	component: SmartLinkElementType,
	resolvedData?: any,
): KnowledgeCardSource {
	const url = component.attributes?.url || '';
	const title = component.attributes?.title;

	return {
		id: resolvedData?.['@id'] || url,
		title: title || resolvedData?.name || url,
		url,
		product: resolvedData?.generator?.name || null,
		type: resolvedData?.['@type'] || 'link',
		iconUrl: resolvedData?.generator?.icon?.url || null,
		lastModified: resolvedData?.dateModified || null,
		spaceName: null,
		spaceUrl: null,
		ari: resolvedData?.ari,
	};
}

export const SmartLinkElement = ({
	component,
	variant,
	isStandalone,
}: SmartLinkComponentProps): React.JSX.Element | null => {
	const url = component.attributes?.url || '';
	const isActionCard = component.attributes?.type === 'action';
	const effectiveVariant = isActionCard ? CardVariant.ACTION_CARD : variant;

	const { objectResolverData } = useFetchSmartLinkData({ urls: [url] });
	const resolvedData = objectResolverData?.[0];

	const source = convertToKnowledgeCardSource(component, resolvedData);

	return (
		<Box xcss={isStandalone && styles.smartLink} testId="dynamic-layout-smart-link">
			<KnowledgeSmartCard source={source} variant={effectiveVariant} />
		</Box>
	);
};
